'use strict'

/**
 * Cross-cluster string-enum constants for the upstream master-lot / contract
 * domain.
 *
 * Lives at the cluster-parent level so both `upstreamContract/` (which gates
 * contract-switch eligibility by lot status) and `upstreamMasterLot/` (which
 * owns the lifecycle proper) can import without creating a back-reference.
 *
 * Owns the enum vocabulary for upstream master-lot / contract logic surfaces —
 * mock API status guards + milestone seeders, display-palette keys,
 * switch-eligibility set, settlement disposition logic, refining-fee basis,
 * reference-fee-schedule impurity band states, and the master-lot view /
 * router CTA gates.
 * Demo fixtures and ad-hoc test data are intentionally NOT migrated; they're
 * the data being fixturized, not consumers of the enum.
 *
 * Defines the client-side enum vocabulary for these surfaces so future
 * server-side mapping has a single anchor. Mock data, specs, and the
 * math/defaulting layer still carry their own basis/state literals; those are
 * not consumers of this module.
 */

export const MASTER_LOT_STATUSES = Object.freeze({
  DRAFT: 'draft',
  // Confirmation gate: minimum validated + simulated SO created.
  CONFIRMED: 'confirmed',
  SHIPPED: 'shipped',
  RECEIVED: 'received',
  MELTED: 'melted',
  PIN_RETURNED: 'pin_returned',
  ASSAYED: 'assayed',
  SETTLED: 'settled',
  CANCELLED: 'cancelled',
})

export const MILESTONE_KINDS = Object.freeze({
  SHIPPED: 'shipped',
  RECEIVED: 'received',
  MELTED: 'melted',
  PIN_RETURNED: 'pin_returned',
  ASSAYED: 'assayed',
  SETTLED: 'settled',
})

export const MILESTONE_STATUSES = Object.freeze({
  PENDING: 'pending',
  COMPLETE: 'complete',
})

export const DISPOSITION_MODES = Object.freeze({
  CASH_INVOICE: 'cash_invoice',
  HELD_ON_PARTNER_ACCOUNT: 'held_on_partner_account',
  TRANSFER_OUT: 'transfer_out',
  PHYSICAL_DELIVERY: 'physical_delivery',
})

// Manifest unit kinds. Bars/pins originate in the source-lot picker; catalog
// rows originate in product search. `manual` remains for legacy snapshots.
export const UNIT_KINDS = Object.freeze({
  BAR: 'bar',
  HOUSE_PIN: 'house_pin',
  CUSTOMER_PIN: 'customer_pin',
  MANUAL: 'manual',
  CATALOG: 'catalog',
})

// Refining-fee basis on a contract tier. CREDITED_FINE_TOZ multiplies the rate
// against per-metal credited fine-toz; NET_WEIGHT_RECEIVED_TOZ against the
// lot's reported gross (Asahi-gold Ag/Pt/Pd: $/ozt net wt received). A
// missing/null basis silently defaults to CREDITED_FINE_TOZ at the math layer
// (forward-compat).
export const FEE_BASIS = Object.freeze({
  CREDITED_FINE_TOZ: 'creditedFineToz',
  NET_WEIGHT_RECEIVED_TOZ: 'netWeightReceivedToz',
})

// Per-band disposition of a reference-fee-schedule impurity charge.
export const IMPURITY_BAND_STATES = Object.freeze({
  FREE: 'free',
  CHARGE: 'charge',
  REJECTED: 'rejected',
  TO_REVIEW: 'to_review',
})

// What a contract's metal-rate tiers qualify on: lot gross weight (toz) or
// per-metal purity content (%).
export const TIER_DIMENSIONS = Object.freeze({
  WEIGHT_TOZ: 'weightToz',
  PURITY_PCT: 'purityPct',
})

// Charge shape for a contract's non-refining "other" fees: a flat per-lot
// amount, a per-toz rate, or a flat amount billed per qualifying event.
export const OTHER_FEE_KINDS = Object.freeze({
  FLAT: 'flat',
  PER_TOZ: 'perToz',
  FLAT_PER_EVENT: 'flatPerEvent',
})

// Chip-state classification for a contract in the picker UI. INVALID covers a
// contract whose effectiveTo can't be strict-parsed as yyyy-mm-dd.
export const CONTRACT_PICKER_STATES = Object.freeze({
  ACTIVE: 'active',
  EXPIRING_SOON: 'expiring_soon',
  PRE_EFFECTIVE: 'pre_effective',
  EXPIRED: 'expired',
  INVALID: 'invalid',
})

// How a master lot's contract changed in its contractHistory log: switched to
// a different contract, or re-snapshotted from the same contract's latest
// version.
export const CONTRACT_HISTORY_KINDS = Object.freeze({
  SWITCH: 'switch',
  RE_SNAPSHOT: 're-snapshot',
})

// Whether the settlement dialog is recording a new settlement or editing the
// existing one — drives hydration source, titles, and the submit action.
export const SETTLEMENT_DIALOG_MODES = Object.freeze({
  CREATE: 'create',
  EDIT: 'edit',
})

// Source-lot reconciliation selection semantics on Master Lots inventory:
// keep selected units on hand (complement ships) vs ship only the selection.
export const UPSTREAM_SOURCE_RECONCILIATION_MODES = Object.freeze({
  KEEP: 'keep',
  SHIP: 'ship',
})
